import type { ReactNode } from "react";
import { FORMAT_DIMENSIONS } from "../../lib/socialCard";
import type { SocialCardFormat } from "../../lib/socialCard";

interface Props {
  format: SocialCardFormat;
  title: string;
  content: ReactNode;
  generatedAt: Date;
  attribution?: string;
  url?: string;
}

const PADDING: Record<SocialCardFormat, number> = {
  square: 64,    // p-16
  landscape: 80, // p-20
  story: 80,     // p-20
};

const TITLE_SIZE: Record<SocialCardFormat, string> = {
  square: "48px",
  landscape: "60px",
  story: "48px",
};

export function SocialCard({
  format,
  title,
  content,
  generatedAt,
  attribution = "READYCAL · REASONZ",
  url = "citypulz.de",
}: Props) {
  const { width, height } = FORMAT_DIMENSIONS[format];
  const pad = PADDING[format];
  const titleFontSize = TITLE_SIZE[format];

  const timestamp = new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(generatedAt);

  return (
    <div
      style={{
        width,
        height,
        padding: pad,
        backgroundColor: "#FAF8F4",
        fontFamily: '"IBM Plex Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Berry-tinted gradient accent — top-right corner */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: Math.round(width * 0.45),
          height: Math.round(height * 0.45),
          background:
            "radial-gradient(ellipse at top right, rgba(219,18,112,0.08) 0%, rgba(219,18,112,0.03) 50%, transparent 72%)",
          pointerEvents: "none",
        }}
      />

      {/* Top: READYCAL tag */}
      <div
        style={{
          fontSize: 20,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontWeight: 600,
          color: "#3A3A40",
          marginBottom: 32,
          flexShrink: 0,
        }}
      >
        READYCAL
      </div>

      {/* Title */}
      <div
        style={{
          fontSize: titleFontSize,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: "#000000",
          lineHeight: 1.1,
          marginBottom: 40,
          flexShrink: 0,
        }}
      >
        {title}
      </div>

      {/* Content block — fills remaining space */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {content}
      </div>

      {/* Bottom row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexShrink: 0,
          marginTop: 32,
          gap: 16,
        }}
      >
        {/* Attribution left */}
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#707078",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            flexShrink: 0,
          }}
        >
          {attribution}
        </span>

        {/* Timestamp centre */}
        <span
          style={{
            fontSize: 12,
            color: "#707078",
            textAlign: "center",
            flex: 1,
          }}
        >
          {timestamp}
        </span>

        {/* URL right */}
        <span
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: "#707078",
            letterSpacing: "0.05em",
            flexShrink: 0,
          }}
        >
          {url}
        </span>
      </div>
    </div>
  );
}
